<template>
  <div class="card p-4 text-center">
    <h3 class="text-muted mb-4" style="font-size: 14px">Incidents</h3>
    <apexchart
      :height="heatmapHeight + 'px'"
      width="95%"
      type="heatmap"
      :series="selectHeatmapSeries"
      :options="chartOptions"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const CompetitionModule = createNamespacedHelpers('competition')

export default {
  name: 'CompetitionHeatmap',
  methods: {
    escapeHtml(unsafe) {
      try {
        const stringUnsafe = unsafe?.toString()
        const safe = stringUnsafe
          ?.replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
        return safe
      } catch (error) {
        console.error(error)
      }
    }
  },
  computed: {
    ...CompetitionModule.mapGetters([
      'selectCategoryName',
      'selectHeatmapCategories',
      'selectHeatmapSeries',
      'selectFilters'
    ]),
    heatmapHeight() {
      const cellHeight = 40
      const xAxisHeight = 68
      const projectedHeight = cellHeight * this.selectHeatmapSeries.length + xAxisHeight
      return projectedHeight
    },
    chartOptions() {
      return {
        chart: {
          type: 'heatmap',
          toolbar: { show: false },
          zoom: { enabled: false },
          events: {
            // this 'click' will be used if we support navigating on clicking lcoation name
            // click: (event, chartContext, config) => {
            //   if (event.path[3].className.baseVal === 'apexcharts-yaxis') {
            //     console.log(event.path[0].innerHTML)
            //   }
            // },
            dataPointSelection: (event, chartContext, config) => {
              // don't do anything if they click on the 'Reviews' or 'Avg online rating' column cells
              if (config.dataPointIndex > 1) {
                const categoryId = this.selectHeatmapCategories[config.dataPointIndex]

                this.$router.push({
                  path: '/competition/details',
                  query: {
                    dateRange: this.$route.query.dateRange,
                    sentiment: 'NEGATIVE',
                    category: categoryId,
                    brand: this.selectHeatmapSeries[config.seriesIndex].competitorId,
                    locs: this.selectFilters.locations,
                    groups: this.selectFilters.groups
                  }
                })
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#12263F']
          },
          formatter: (val, { seriesIndex, dataPointIndex }) => {
            if (dataPointIndex === 0) {
              // Reviews column - show count
              return this.selectHeatmapSeries[seriesIndex].counts[0]
            } else if (dataPointIndex === 1) {
              // Avg online rating column - show rating
              return this.selectHeatmapSeries[seriesIndex].counts[1]
                ? this.selectHeatmapSeries[seriesIndex].counts[1].toFixed(1)
                : '0.0'
            } else {
              // Other columns - show percentage
              return val ? val + '%' : '0%'
            }
          }
        },
        xaxis: {
          categories: this.selectHeatmapCategories,
          tooltip: { enabled: false },
          position: 'top',
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            formatter: (val) => {
              // Return the category name directly
              return this.selectCategoryName(val)
            },
            trim: true,
            rotate: 0, // Add rotation to prevent label overlap
            rotateAlways: false,
            hideOverlappingLabels: false,
            maxHeight: 100
          }
        },
        yaxis: {
          labels: {
            formatter: (val) => {
              // Use the label directly from the API data
              return this.escapeHtml(val)
            },
            minWidth: 80,
            maxWidth: 100
          },
          reversed: false // Ensure data is not reversed
        },
        legend: { show: false },
        tooltip: {
          enabled: true,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const seriesData = this.selectHeatmapSeries[seriesIndex]
            const incidentCount = seriesData.counts[dataPointIndex]
            const totalCount = seriesData.totalCount

            if (dataPointIndex === 0) {
              // Reviews column
              return `${incidentCount} ${incidentCount === 1 ? 'review' : 'reviews'}`
            } else if (dataPointIndex === 1) {
              // Avg online rating column
              return `${incidentCount ? incidentCount.toFixed(1) : '0.0'} average rating`
            } else {
              // Other columns - incidents
              return `${incidentCount} ${incidentCount === 1 ? 'incident' : 'incidents'}
              out of ${totalCount} ${totalCount === 1 ? 'piece' : 'pieces'} of feedback`
            }
          }
        },
        plotOptions: {
          heatmap: {
            radius: 0,
            enableShades: false,
            colorScale: {
              ranges: [
                {
                  from: -1,
                  to: 0,
                  color: '#FFFFFF'
                },
                {
                  from: 1,
                  to: 5,
                  color: '#FED2DA'
                },
                {
                  from: 6,
                  to: 10,
                  color: '#FDA5B5'
                },
                {
                  from: 11,
                  to: 100,
                  color: '#FB4B6B'
                }
              ]
            }
          }
        }
      }
    }
  }
}
</script>

<style scoped>
#line {
  color: white;
}
.bg-light-green {
  background: #ccf7e5;
}
.bg-light-red {
  background: #ffcccb;
}
</style>
